/**
 * Bill of materials straight from a room's positioned sections.
 *
 * This is the designer's costing entry point: whatever is laid out on the walls
 * and ceiling — however it was edited — goes in, and a bill of materials with
 * cost totals comes out. Unlike `estimateViaSections` it does not need the
 * wizard's inputs, so it works for layouts the wizard could never describe.
 *
 * Covers panels (Wall&CeilingBoard and SlatWall), the H-Trim their joints need,
 * the trim on every section edge (trims.ts — J-Trim and H-Trim by default, or
 * whatever the user chose for an edge), and screws.
 *
 * `issues` lists any section that breaks a placement rule. The designer never
 * produces one, but a stored layout might — callers can refuse or flag it.
 */

import { BillOfMaterials, CostTotals } from '../../types';
import { RoomSurfaces } from '../../types/sections';
import { consolidateMaterialItems, createEmptyBOM, calculateCostTotals } from '../consolidation';
import { calculateRoomPanels } from './panelCalc';
import { roomTrimMaterials } from './trims';
import { calculateScrewsFromBOM } from '../screws';
import { SectionIssue, validateRoom } from './validation';

export interface SurfacesEstimate {
  billOfMaterials: BillOfMaterials;
  totals: CostTotals;
  issues: SectionIssue[];
}

export function calculateFromSurfaces(room: RoomSurfaces): SurfacesEstimate {
  const panels = calculateRoomPanels(room);
  const billOfMaterials = createEmptyBOM();
  billOfMaterials.wallAndCeilingBoard = consolidateMaterialItems(panels.wcbPanels);
  billOfMaterials.slatwall = consolidateMaterialItems(panels.slatWallPanels);

  // Joint H-Trim comes out of the panel maths; edge trim from the section edges.
  const edgeTrims = roomTrimMaterials(room);
  billOfMaterials.hTrims = consolidateMaterialItems([...panels.hTrims, ...edgeTrims.hTrims]);
  billOfMaterials.jTrimsSnapIn = consolidateMaterialItems(edgeTrims.jTrimsSnapIn);
  billOfMaterials.otherTrims = consolidateMaterialItems(edgeTrims.otherTrims);
  billOfMaterials.screws = calculateScrewsFromBOM(billOfMaterials);

  return {
    billOfMaterials,
    totals: calculateCostTotals(billOfMaterials),
    issues: validateRoom(room),
  };
}
